import React from 'react'
import { Company } from 'models'
import { entityDisplayName } from 'utils/helpers'
import { ALL_ACCOUNTS_ID } from '../../constants'
import { companiesActions, type CompaniesReducerAction } from '../../reducer'
import type { CompaniesReducerState } from '../../types'

const useSelectionSideEffects = (params: {
  state: CompaniesReducerState
  dispatch: React.Dispatch<CompaniesReducerAction>
  userCompany: Company | null
  b2cAutoSelectedChildId: string
  b2cAutoSelectedChild: Company | null
  isLoginB2C: boolean
  setParentSearchValue: (value: string, options?: { skipCommit?: boolean }) => void
  setParentSearchFilterValue: (value: string) => void
  resetChildrenSearch: () => void
}) => {
  const {
    state,
    dispatch,
    userCompany,
    b2cAutoSelectedChildId,
    b2cAutoSelectedChild,
    isLoginB2C,
    setParentSearchValue,
    setParentSearchFilterValue,
    resetChildrenSearch,
  } = params
  const {
    selectedParent,
    selectedChildren,
    pendingSelection,
    isAllAccountsSelected,
    isOrphanSelected,
  } = state

  React.useEffect(() => {
    resetChildrenSearch()
  }, [resetChildrenSearch, selectedParent?.id, isOrphanSelected])

  React.useEffect(() => {
    dispatch({ type: companiesActions.UPDATE_FILTERING_IDS })
  }, [dispatch, selectedParent, selectedChildren, isAllAccountsSelected, isOrphanSelected])

  React.useEffect(() => {
    if (!isLoginB2C || !userCompany) return

    const shouldSetB2CParent = selectedParent?.id !== userCompany.id
    if (shouldSetB2CParent) {
      dispatch({ type: companiesActions.SET_SELECTED_PARENT, entity: userCompany })
      setParentSearchValue(entityDisplayName(userCompany, true), { skipCommit: true })
      setParentSearchFilterValue('')
      return
    }

    if (!b2cAutoSelectedChildId) return
    if (selectedChildren.length > 0 || pendingSelection.length > 0) return

    const selectableChildren = state.childrenCompanies.filter(
      (company) => company.id !== ALL_ACCOUNTS_ID,
    )
    const defaultChildFromList = selectableChildren.find(
      (company) => company.id === b2cAutoSelectedChildId,
    )
    const defaultChild = defaultChildFromList || b2cAutoSelectedChild
    if (!defaultChild) return

    dispatch({
      type: companiesActions.SET_PENDING_SELECTION,
      companies: [defaultChild],
      isAllAccountsSelected: false,
    })
    dispatch({
      type: companiesActions.APPLY_SELECTION,
      companies: [defaultChild],
      isAllAccountsSelected: false,
    })
  }, [
    b2cAutoSelectedChild,
    b2cAutoSelectedChildId,
    dispatch,
    isLoginB2C,
    selectedParent?.id,
    pendingSelection.length,
    selectedChildren.length,
    setParentSearchFilterValue,
    setParentSearchValue,
    state.childrenCompanies,
    userCompany,
  ])

  React.useEffect(() => {
    if (isLoginB2C) return

    if (selectedParent) {
      setParentSearchValue(entityDisplayName(selectedParent, true), { skipCommit: true })
      setParentSearchFilterValue('')
    } else {
      setParentSearchValue('')
      setParentSearchFilterValue('')
    }
  }, [isLoginB2C, selectedParent, setParentSearchFilterValue, setParentSearchValue])
}

export { useSelectionSideEffects }
